import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { SerialService } from './serial.service';

const MIN_RTC_FIRMWARE_VERSION = 1.011;
const RTC_READBACK_TOLERANCE_SECONDS = 2;
const CALIBRATION_READBACK_TOLERANCE_PPM = 0.5;

export type DeviceDateTimeInfo = {
    deviceDateTime: string | null;
    deviceDateTimeRequestedAt: string | null;
};

@Injectable({
    providedIn: 'root'
})
export class DeviceInfoService {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public constructor() {}

    /**
     * Retrieves the device serial number by sending debug commands
     * 1. Optionally sends "login enabledebugging" if login is required
     * 2. Sends "getsernumb" to request serial number
     * 3. Waits for response: "Device Serial number:XXXXXX"
     * @param serialService - The serial service instance
     * @param loginRequired - Whether login is required before getting serial number
     * @param password - The password to use for login (if required)
     * @param timeoutMs - Timeout for waiting for response
     * @returns Promise<string> - The device serial number (e.g., "000037")
     * @throws Error if serial number retrieval fails
     */
    public async getDeviceSerialNumber(
        serialService: SerialService,
        loginRequired: boolean = false,
        password: string | null = null,
        timeoutMs: number = 2000
    ): Promise<string> {
        console.log(`[DeviceInfo] Starting serial number retrieval... (loginRequired: ${loginRequired})`);

        try {
            // Step 1: Conditionally send login command
            if (loginRequired) {
                const loginCmd = password ? `login ${password}` : 'login enabledebugging';
                console.log(`[DeviceInfo] Sending: ${loginCmd}`);
                void serialService.sendData(new TextEncoder().encode(loginCmd + '\r\n'));

                // Wait for initial response (give it 2 seconds)
                const response = await serialService.waitForTextResponse(timeoutMs);
                console.log(`[DeviceInfo] Response to login: "${response}"`);

                // Consume any additional buffered output/prompt from login command
                // The device might send multiple lines or a prompt after login
                console.log('[DeviceInfo] Consuming additional login output...');
                await this.consumeAdditionalDeviceOutput(serialService, 200);
                console.log('[DeviceInfo] Login output consumed, buffer cleared');
            } else {
                console.log('[DeviceInfo] Login not required, proceeding directly to getsernumb');
            }

            // Step 2: Send getsernumb command
            console.log('[DeviceInfo] Sending: getsernumb');
            void serialService.sendData(new TextEncoder().encode('getsernumb\r\n'));

            // The device may echo the command back. Skip the echo line.
            // Use shorter timeout since we know responses are quick
            const echo = await serialService.waitForTextResponse(1000);
            console.log(`[DeviceInfo] Echo/first line: "${echo}"`);

            // If the echo looks like it contains the command name, read the next line for the actual response
            let serialNumberLine = echo;
            if (echo.toLowerCase().includes('getsernumb') || !echo.includes('Device Serial number')) {
                console.log('[DeviceInfo] Command echo detected, reading next line for actual response...');
                serialNumberLine = await serialService.waitForTextResponse(1000);
                console.log(`[DeviceInfo] Actual response: "${serialNumberLine}"`);
            }

            // Step 3: Parse serial number from response
            // Expected format: "Device Serial number:000037"
            const serialNumber = this.parseSerialNumber(serialNumberLine);
            if (!serialNumber) {
                throw new Error(
                    `Invalid response format. Expected "Device Serial number:XXXXXX", got: "${serialNumberLine}"`
                );
            }

            console.log(`[DeviceInfo] ✓ Successfully retrieved serial number: ${serialNumber}`);
            return serialNumber;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error(`[DeviceInfo] ✗ Failed to retrieve serial number: ${errorMsg}`);
            throw new Error(`Failed to retrieve device serial number: ${errorMsg}`);
        }
    }

    public async getDeviceDateTime(
        serialService: SerialService,
        softwareVersion: string,
        timeoutMs = 2000
    ): Promise<DeviceDateTimeInfo> {
        if (!this.supportsDeviceDateTime(softwareVersion)) {
            return { deviceDateTime: null, deviceDateTimeRequestedAt: null };
        }

        console.log('[DeviceInfo] Sending: rtcget');
        const deviceDateTimeRequestedAt = new Date().toISOString();
        const deadline = Date.now() + timeoutMs;

        try {
            const sent = await serialService.sendData(new TextEncoder().encode('rtcget\r\n'));
            if (!sent) {
                throw new Error('Could not send rtcget to the device');
            }

            while (Date.now() < deadline) {
                const line = await serialService.waitForTextResponse(Math.max(1, deadline - Date.now()));
                const match = line.match(/RTC time:\s*(\d{4}-\d{2}-\d{2})\s*T(\d{2}:\d{2}:\d{2})/i);
                if (!match) {
                    continue;
                }

                console.log(`[DeviceInfo] RTC response: "${line}"`);
                const dateTime = DateTime.fromISO(`${match[1]}T${match[2]}`, { zone: 'utc' });
                if (!dateTime.isValid) {
                    throw new Error('Device returned an invalid RTC datetime');
                }

                return {
                    deviceDateTime: dateTime.toISO({ includeOffset: false, suppressMilliseconds: true }),
                    deviceDateTimeRequestedAt
                };
            }

            throw new Error('Timed out waiting for device RTC datetime');
        } catch (error: unknown) {
            const errorMsg = error instanceof Error ? error.message : JSON.stringify(error);
            console.warn(`[DeviceInfo] Failed to retrieve RTC datetime: ${errorMsg}`);
            return { deviceDateTime: null, deviceDateTimeRequestedAt };
        }
    }

    public getDeviceClockDifference(reading: DeviceDateTimeInfo): number | null {
        if (!reading.deviceDateTime || !reading.deviceDateTimeRequestedAt) return null;
        const deviceTime = DateTime.fromISO(reading.deviceDateTime, { zone: 'utc' });
        const requestedAt = DateTime.fromISO(reading.deviceDateTimeRequestedAt, { setZone: true });
        if (!deviceTime.isValid || !requestedAt.isValid || requestedAt.offset !== 0) return null;

        const localTime = requestedAt.toLocal().startOf('second');
        const localWallTime = DateTime.fromObject(localTime.toObject(), { zone: 'utc' });
        return deviceTime.diff(localWallTime, 'seconds').seconds;
    }

    public async setDeviceDateTime(
        serialService: SerialService,
        softwareVersion: string,
        canContinue: () => boolean
    ): Promise<DeviceDateTimeInfo> {
        if (!this.supportsDeviceDateTime(softwareVersion)) {
            throw new Error('The device firmware does not support RTC commands');
        }

        await this.prepareDeviceMaintenance(serialService, canContinue);
        const localTime = DateTime.local();
        const command = `rtcset ${localTime.toFormat('yy MM dd HH mm ss')}\r\n`;
        console.log(`[DeviceInfo] Sending: ${command.trim()} (client timezone: ${localTime.zoneName})`);
        const sent = await serialService.sendData(new TextEncoder().encode(command));
        if (!sent) throw new Error('Could not send rtcset to the device');

        await this.consumeAdditionalDeviceOutput(serialService);
        if (!canContinue()) throw new Error('Clock synchronization was cancelled');
        const reading = await this.getDeviceDateTime(serialService, softwareVersion);
        const difference = this.getDeviceClockDifference(reading);
        if (difference === null) {
            throw new Error('The device clock could not be confirmed: rtcget returned no valid datetime');
        }
        if (Math.abs(difference) > RTC_READBACK_TOLERANCE_SECONDS) {
            const clientTime = DateTime.fromISO(reading.deviceDateTimeRequestedAt).toLocal();
            throw new Error(
                `The device clock could not be confirmed: device=${reading.deviceDateTime}, ` +
                    `client=${clientTime.toFormat('yyyy-MM-dd HH:mm:ss')} (${clientTime.zoneName}), ` +
                    `difference=${difference}s (allowed ${RTC_READBACK_TOLERANCE_SECONDS}s)`
            );
        }
        return reading;
    }

    public async prepareDeviceMaintenance(serialService: SerialService, canContinue: () => boolean): Promise<void> {
        if (!canContinue()) throw new Error('Device maintenance was cancelled');
        await this.consumeAdditionalDeviceOutput(serialService);
        if (!canContinue()) throw new Error('Device maintenance was cancelled');
        console.log('[DeviceInfo] Logging in for device maintenance');
        const sent = await serialService.sendData(new TextEncoder().encode('login enableMaintenance\r\n'));
        if (!sent) throw new Error('Could not send maintenance login to the device');
        await serialService.waitForTextResponse(2000);
        await this.consumeAdditionalDeviceOutput(serialService);
        if (!canContinue()) throw new Error('Device maintenance was cancelled');
    }

    public calculateCalibrationTarget(hardwareCorrection: number, signedDrift: number): number {
        if (!Number.isFinite(hardwareCorrection) || !Number.isFinite(signedDrift)) {
            throw new Error('A finite hardware correction and signed drift are required');
        }
        const value = -hardwareCorrection + signedDrift;
        const rounded = Math.sign(value) * Math.round(Math.abs(value));
        if (!Number.isSafeInteger(rounded)) throw new Error('The calculated calibration offset is invalid');
        return rounded === 0 ? 0 : rounded;
    }

    public isDeviceCalibrationMatch(correction: number, targetOffset: number): boolean {
        return this.isCalibrationCorrectionMatch(correction, -targetOffset);
    }

    public isCalibrationCorrectionMatch(actual: number, expected: number): boolean {
        return (
            Number.isFinite(actual) &&
            Number.isFinite(expected) &&
            Math.abs(actual - expected) <= CALIBRATION_READBACK_TOLERANCE_PPM
        );
    }

    public async getDeviceCalibration(
        serialService: SerialService,
        softwareVersion: string,
        canContinue: () => boolean
    ): Promise<number> {
        if (!this.supportsDeviceDateTime(softwareVersion) || !canContinue()) {
            throw new Error('RTC calibration is unavailable for this device connection');
        }
        const sent = await serialService.sendData(new TextEncoder().encode('rtcgetCalib\r\n'));
        if (!sent) throw new Error('Could not send rtcgetCalib to the device');
        const deadline = Date.now() + 2000;
        while (Date.now() < deadline) {
            if (!canContinue()) throw new Error('Device maintenance was cancelled');
            const line = await serialService.waitForTextResponse(Math.max(1, deadline - Date.now()));
            const match = line.match(/RTC Calibration:.*?([+-]?\d+(?:\.\d+)?)\s*ppm\s+correction/i);
            if (!match) continue;
            const correction = Number(match[1]);
            if (!Number.isFinite(correction)) throw new Error('Invalid RTC hardware correction');
            return correction;
        }
        throw new Error('Timed out waiting for RTC calibration information');
    }

    public async setDeviceCalibration(
        serialService: SerialService,
        softwareVersion: string,
        targetOffset: number,
        canContinue: () => boolean,
        onWriteAttempt: () => void
    ): Promise<number> {
        if (!this.supportsDeviceDateTime(softwareVersion) || !Number.isSafeInteger(targetOffset)) {
            throw new Error('The calibration offset must be a valid integer for supported firmware');
        }
        await this.prepareDeviceMaintenance(serialService, canContinue);
        if (!canContinue()) throw new Error('Device maintenance was cancelled');
        onWriteAttempt();
        const sent = await serialService.sendData(new TextEncoder().encode(`rtcsetCalib ${targetOffset}\r\n`));
        if (!sent) throw new Error('Could not send rtcsetCalib to the device');
        await this.consumeAdditionalDeviceOutput(serialService);
        const correction = await this.getDeviceCalibration(serialService, softwareVersion, canContinue);
        if (!this.isDeviceCalibrationMatch(correction, targetOffset)) {
            throw new Error('RTC calibration readback does not match the requested offset');
        }
        return correction;
    }

    private supportsDeviceDateTime(softwareVersion: string): boolean {
        const version = Number(softwareVersion.trim().match(/^\d+\.\d+(?=$|[-.+])/)?.[0]);
        return Number.isFinite(version) && version >= MIN_RTC_FIRMWARE_VERSION;
    }

    /**
     * Consumes additional buffered output from device (prompts, additional lines, etc.)
     * Attempts to read additional lines with a very short timeout until no more data arrives
     * @param serialService - The serial service instance
     * @param timeoutMs - Total timeout for consuming additional output
     */
    private async consumeAdditionalDeviceOutput(serialService: SerialService, timeoutMs: number = 300): Promise<void> {
        const startTime = Date.now();
        const maxTotalTime = timeoutMs;
        const perLineTimeout = 50; // Very short timeout per line attempt

        while (Date.now() - startTime < maxTotalTime) {
            try {
                // Try to consume another line with very short timeout
                const additionalLine = await serialService.waitForTextResponse(perLineTimeout);
                console.log(`[DeviceInfo] Consumed additional line: "${additionalLine}"`);
            } catch {
                // Timeout on this line = no more data available, which is what we want
                console.log('[DeviceInfo] No more buffered lines to consume');
                break;
            }
        }

        // Final drain to ensure buffer is clean
        serialService.drainBuffer();
    }

    /**
     * Retrieves the firmware version information from device
     * Sends "version" command and reads multi-line response
     * @param serialService - The serial service instance
     * @param timeoutMs - Timeout for waiting for response
     * @returns Promise<string> - The firmware version text (multi-line)
     * @throws Error if version retrieval fails
     */
    public async getDeviceVersion(serialService: SerialService): Promise<string> {
        console.log('[DeviceInfo] Starting firmware version retrieval...');

        try {
            console.log('[DeviceInfo] Sending: version');
            void serialService.sendData(new TextEncoder().encode('version\r\n'));

            // Small delay to ensure echo transmission completes
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Read the version response - it's multi-line, so we'll keep reading until we get a timeout
            let versionText = '';
            let versionDataStarted = false;
            let lineCount = 0;
            const maxLines = 15; // Increased to handle both formats with potential extra lines
            const expectedVersionPrefixes = ['Software', 'Main', 'Core', 'Resources', 'Languages', 'HW'];

            while (lineCount < maxLines) {
                try {
                    // Try to read another line with a short timeout
                    const line = await serialService.waitForTextResponse(500);
                    console.log(`[DeviceInfo] Version line ${lineCount + 1}: "${line}"`);

                    // Skip corrupted or echo lines at the beginning
                    if (!versionDataStarted) {
                        // We haven't started version data yet, check if this line is the echo or garbage
                        const lowerLine = line.toLowerCase();

                        // Check if this line looks like the echo (contains "version" but no actual version data)
                        if (lowerLine.includes('version') && !line.includes(':')) {
                            console.log('[DeviceInfo] Skipping echo line (contains "version" but no data)');
                            continue; // Skip and read next line
                        }

                        // Check for error indicators
                        if (lowerLine.includes('unknown command') || lowerLine.includes('error')) {
                            console.warn(`[DeviceInfo] ✗ Command was corrupted or not received properly: "${line}"`);
                            throw new Error(
                                `Command execution failed: ${line}. Buffer may not have been cleared properly.`
                            );
                        }

                        // Check if this line starts with an expected version prefix (handle both normal and indented)
                        const trimmedLine = line.trim();
                        const startsWithExpectedPrefix = expectedVersionPrefixes.some((prefix) =>
                            trimmedLine.startsWith(prefix)
                        );

                        if (startsWithExpectedPrefix) {
                            versionDataStarted = true;
                            console.log('[DeviceInfo] ✓ Version data started');
                        } else if (line.trim().length > 0) {
                            // Non-empty line that doesn't match echo or expected prefix - skip as garbage
                            console.log('[DeviceInfo] Skipping unexpected line, waiting for version data');
                            continue;
                        }
                    }

                    // Once version data has started, collect all lines (including indented ones like Build Date, Commit)
                    if (versionDataStarted) {
                        versionText += line + '\n';
                        lineCount++;
                    }
                } catch (error) {
                    // Timeout = no more lines
                    if (error instanceof Error && error.message.includes('Buffer may not')) {
                        throw error; // Re-throw our custom error
                    }
                    console.log('[DeviceInfo] Version response complete');
                    break;
                }
            }

            const finalVersion = versionText.trim();
            console.log(`[DeviceInfo] ✓ Successfully retrieved firmware version:\n${finalVersion}`);
            return finalVersion;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error(`[DeviceInfo] ✗ Failed to retrieve version: ${errorMsg}`);
            throw new Error(`Failed to retrieve device version: ${errorMsg}`);
        }
    }

    /**
     * Parses the serial number from device response
     * Expected format: "Device Serial number:XXXXXX"
     * @param response - The response string from device
     * @returns The serial number string (e.g., "000037") or null if not found
     */
    private parseSerialNumber(response: string): string | null {
        const match = response.match(/Device\s+Serial\s+number:\s*(\S+)/i);
        return match?.[1]?.trim() ?? null;
    }
}
